import { useMemo, useState } from "react";
import { X, ArrowUpRight, MapPin, Ruler, FileText } from "lucide-react";
import { GALLERY_ITEMS } from "../../lib/constants";
import CaseStudiesModal from "./CaseStudiesModal";

// Industrial hidden per client decision — filter kept in data for future use.
const VISIBLE_FILTERS = [
  { id: "todos", label: "Todos" },
  { id: "canchas", label: "Canchas" },
  { id: "comercial", label: "Comercial" },
  { id: "educativo", label: "Educativo" },
  { id: "residencial", label: "Residencial" },
];

export default function Gallery() {
  const [filter, setFilter] = useState("todos");
  const [active, setActive] = useState(null);
  const [caseModalOpen, setCaseModalOpen] = useState(false);

  const items = useMemo(() => {
    if (filter === "todos") return GALLERY_ITEMS;
    return GALLERY_ITEMS.filter((i) => i.cat === filter);
  }, [filter]);

  const scrollToForm = () => {
    const el = document.getElementById("cotiza");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="proyectos" data-testid="gallery-section" className="bg-[#FAFAF7] text-[#1F2937] section-pad">
      <div className="container-tight">
        <div className="flex items-center gap-3 mb-4">
          <span className="divider-accent" />
          <span className="kicker">Galería</span>
        </div>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div>
            <h2 className="font-display font-black tracking-tightest text-4xl md:text-5xl lg:text-6xl max-w-3xl">
              Más de <span className="text-accent">100 proyectos</span> realizados.
            </h2>
            <p className="mt-6 max-w-2xl text-[#1F2937]/70 text-base md:text-lg">
              Arquitectura textil instalada en México y Latinoamérica.
            </p>
          </div>
        </div>

        <div className="mt-10 flex flex-wrap gap-2" data-testid="gallery-filters">
          {VISIBLE_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`font-display font-bold uppercase tracking-wider text-xs px-5 py-3 border transition-colors ${
                filter === f.id
                  ? "bg-accent border-accent text-white"
                  : "border-[#DDE2D5] text-[#1F2937]/80 hover:border-accent hover:text-accent"
              }`}
              data-testid={`gallery-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="mt-10 columns-1 sm:columns-2 lg:columns-3 gap-4 [column-fill:_balance]">
          {items.map((it, i) => (
            <button
              key={it.src + i}
              onClick={() => setActive(it)}
              className="block mb-4 break-inside-avoid w-full text-left group relative overflow-hidden border border-[#DDE2D5] hover:border-accent transition-colors"
              data-testid={`gallery-item-${i}`}
            >
              <div className="img-zoom-wrap">
                <img
                  src={it.src}
                  alt={`${it.title} — ${it.location}`}
                  loading="lazy"
                  className="w-full h-auto object-cover"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-eclipse via-eclipse/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="absolute bottom-0 left-0 right-0 p-5 opacity-0 group-hover:opacity-100 transition-opacity">
                <h4 className="font-display font-black text-[#1F2937] text-lg uppercase tracking-tight">{it.title}</h4>
                <p className="text-[#1F2937]/70 text-xs font-mono mt-1">{it.location} · {it.m2}</p>
              </div>
              <span className="absolute top-3 right-3 w-9 h-9 grid place-items-center bg-accent text-white opacity-0 group-hover:opacity-100 transition-opacity">
                <ArrowUpRight className="w-4 h-4" />
              </span>
            </button>
          ))}
        </div>

        <div className="mt-14 space-y-4">
          <button
            onClick={scrollToForm}
            className="inline-flex items-center gap-3 font-display font-black text-lg md:text-2xl text-[#1F2937] group text-left"
            data-testid="gallery-bottom-cta"
          >
            Tu espacio puede ser el próximo proyecto de Velarias Tens.
            <span className="text-accent group-hover:translate-x-1 transition-transform">Cotiza gratis →</span>
          </button>

          <div>
            <button
              onClick={() => setCaseModalOpen(true)}
              className="inline-flex items-center gap-2 font-mono uppercase text-[11px] tracking-[0.25em] text-[#1F2937]/60 hover:text-accent transition-colors"
              data-testid="gallery-case-studies-cta"
            >
              <FileText className="w-3.5 h-3.5" />
              Recibe 5 casos de éxito con m², materiales y resultados — déjanos tu email →
            </button>
          </div>
        </div>
      </div>

      <CaseStudiesModal open={caseModalOpen} onClose={() => setCaseModalOpen(false)} />

      {active && (
        <div
          className="fixed inset-0 bg-eclipse/95 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          onClick={() => setActive(null)}
          data-testid="gallery-lightbox"
        >
          <button
            className="absolute top-6 right-6 text-[#1F2937]/80 hover:text-accent"
            onClick={() => setActive(null)}
            data-testid="gallery-lightbox-close"
            aria-label="Cerrar"
          >
            <X className="w-8 h-8" />
          </button>
          <div
            className="max-w-6xl w-full flex flex-col lg:flex-row gap-6 items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img src={active.src} alt={active.title} className="max-h-[80vh] w-auto object-contain border border-[#DDE2D5]" />
            <div className="lg:max-w-sm">
              <span className="kicker">Proyecto</span>
              <h3 className="mt-2 font-display font-black text-3xl md:text-4xl text-[#1F2937] tracking-tight">{active.title}</h3>
              <div className="mt-6 space-y-3 text-[#1F2937]/80">
                <div className="flex items-center gap-3"><MapPin className="w-4 h-4 text-accent" /> {active.location}</div>
                <div className="flex items-center gap-3"><Ruler className="w-4 h-4 text-accent" /> {active.m2}</div>
              </div>
              <button
                onClick={() => { setActive(null); scrollToForm(); }}
                className="btn-accent mt-8"
              >
                Cotizar algo similar
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
